import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { EnergyIcon, SleepIcon, ActivityIcon, RecoveryIcon } from './Icons'
import { useLanguage } from '../contexts/LanguageContext'

const Analytics = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.2 })
  const { t } = useLanguage()

  const metrics = [
    {
      label: t.analytics.energy,
      value: 85,
      color: 'from-primary to-primary-dark',
      iconType: 'energy',
    },
    {
      label: t.analytics.sleepQuality,
      value: 92,
      color: 'from-primary to-primary-dark',
      iconType: 'sleep',
    },
    {
      label: t.analytics.activity,
      value: 78,
      color: 'from-primary to-primary-dark',
      iconType: 'activity',
    },
    {
      label: t.analytics.recovery,
      value: 88,
      color: 'from-primary to-primary-dark',
      iconType: 'recovery',
    },
  ]

  const weeklyData = [
    { day: t.analytics.days.mon, sleep: 7.2, energy: 82, activity: 75, recovery: 80 },
    { day: t.analytics.days.tue, sleep: 7.5, energy: 85, activity: 78, recovery: 83 },
    { day: t.analytics.days.wed, sleep: 8.1, energy: 88, activity: 82, recovery: 87 },
    { day: t.analytics.days.thu, sleep: 7.8, energy: 87, activity: 80, recovery: 85 },
    { day: t.analytics.days.fri, sleep: 8.0, energy: 90, activity: 85, recovery: 89 },
    { day: t.analytics.days.sat, sleep: 8.5, energy: 92, activity: 88, recovery: 91 },
    { day: t.analytics.days.sun, sleep: 8.2, energy: 89, activity: 82, recovery: 88 },
  ]

  const maxValue = 100
  const minValue = 70
  const range = maxValue - minValue

  return (
    <section
      ref={ref}
      className="min-h-screen py-8 md:py-12 lg:py-16 xl:py-24 bg-gradient-to-b from-white via-emerald-50/30 to-white relative overflow-hidden flex items-center"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 md:mb-12 lg:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-3 md:mb-4 lg:mb-6 text-gray-900">
            {t.analytics.title}
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto px-4">
            {t.analytics.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 lg:gap-8 mb-6 md:mb-8 lg:mb-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-8 border-2 border-gray-100 premium-shadow"
          >
            <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-gray-900">
              {t.analytics.currentMetrics}
            </h3>
            <div className="space-y-3 md:space-y-4 lg:space-y-6">
              {metrics.map((metric, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1.5 md:mb-2">
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <span className="flex-shrink-0 w-4 h-4 md:w-6 md:h-6 flex items-center justify-center">
                        {metric.iconType === 'energy' && (
                          <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                              fill={`url(#analytics-energy-gradient-${index})`}
                              stroke={`url(#analytics-energy-gradient-${index})`}
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <defs>
                              <linearGradient id={`analytics-energy-gradient-${index}`} x1="3" y1="14" x2="21" y2="10">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="100%" stopColor="#34d399" />
                              </linearGradient>
                            </defs>
                          </svg>
                        )}
                        {metric.iconType === 'sleep' && (
                          <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
                              fill={`url(#analytics-sleep-gradient-${index})`}
                              stroke={`url(#analytics-sleep-gradient-${index})`}
                              strokeWidth="1.5"
                            />
                            <path
                              d="M9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12Z"
                              fill="white"
                            />
                            <defs>
                              <linearGradient id={`analytics-sleep-gradient-${index}`} x1="11.21" y1="3" x2="21" y2="12.79">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="100%" stopColor="#059669" />
                              </linearGradient>
                            </defs>
                          </svg>
                        )}
                        {metric.iconType === 'activity' && (
                          <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M22 12H18L15 21L9 3L6 12H2"
                              stroke={`url(#analytics-activity-gradient-${index})`}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <defs>
                              <linearGradient id={`analytics-activity-gradient-${index}`} x1="2" y1="3" x2="22" y2="21">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="100%" stopColor="#059669" />
                              </linearGradient>
                            </defs>
                          </svg>
                        )}
                        {metric.iconType === 'recovery' && (
                          <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
                              fill={`url(#analytics-recovery-gradient-${index})`}
                            />
                            <defs>
                              <linearGradient id={`analytics-recovery-gradient-${index}`} x1="1.55" y1="3" x2="22.45" y2="21.23">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="100%" stopColor="#059669" />
                              </linearGradient>
                            </defs>
                          </svg>
                        )}
                      </span>
                      <span className="font-semibold text-sm md:text-base text-gray-700">
                        {metric.label}
                      </span>
                    </div>
                    <span className="font-bold text-sm md:text-base gradient-text">{metric.value}%</span>
                  </div>
                  <div className="h-2 md:h-3 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={isInView ? { width: `${metric.value}%` } : {}}
                      transition={{ delay: 0.4 + index * 0.1, duration: 1 }}
                      className={`h-full bg-gradient-to-r ${metric.color} rounded-full`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl md:rounded-3xl p-4 md:p-6 lg:p-8 border-2 border-gray-100 premium-shadow"
          >
            <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-gray-900">
              {t.analytics.weeklyDynamics}
            </h3>
            <div className="space-y-3 md:space-y-6">
              <div className="relative h-40 md:h-48 lg:h-64 xl:h-80 w-full overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0">
                <svg className="w-full h-full min-w-[320px] sm:min-w-[400px] md:min-w-[500px] lg:min-w-[600px]" viewBox="0 0 600 300" preserveAspectRatio="xMidYMid meet">
                  <defs>
                    <linearGradient id="sleepLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#059669" />
                    </linearGradient>
                    <linearGradient id="energyLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#34d399" />
                      <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                    <linearGradient id="activityLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#6ee7b7" />
                      <stop offset="100%" stopColor="#34d399" />
                    </linearGradient>
                    <linearGradient id="recoveryLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#a7f3d0" />
                      <stop offset="100%" stopColor="#6ee7b7" />
                    </linearGradient>
                  </defs>
                  
                  <g>
                    {[70, 75, 80, 85, 90, 95, 100].map((value, i) => {
                      const y = 250 - ((value - minValue) / range) * 200
                      return (
                        <g key={i}>
                          <line
                            x1="40"
                            y1={y}
                            x2="560"
                            y2={y}
                            stroke="#e5e7eb"
                            strokeWidth="1"
                            strokeDasharray="2,2"
                          />
                          <text
                            x="35"
                            y={y + 4}
                            textAnchor="end"
                            fontSize="10"
                            fill="#9ca3af"
                            className="font-semibold"
                          >
                            {value}%
                          </text>
                        </g>
                      )
                    })}
                  </g>
                  
                  {weeklyData.map((data, index) => {
                    const x = 40 + (index / (weeklyData.length - 1)) * 520
                    return (
                      <g key={index}>
                        <line
                          x1={x}
                          y1="20"
                          x2={x}
                          y2="250"
                          stroke="#e5e7eb"
                          strokeWidth="1"
                        />
                        <text
                          x={x}
                          y="280"
                          textAnchor="middle"
                          fontSize="12"
                          fill="#6b7280"
                          className="font-semibold"
                        >
                          {data.day}
                        </text>
                      </g>
                    )
                  })}
                  
                  {[
                    { key: 'sleep', color: '#10b981', gradient: 'sleepLineGradient', value: (d) => d.sleep * 10 },
                    { key: 'energy', color: '#34d399', gradient: 'energyLineGradient', value: (d) => d.energy },
                    { key: 'activity', color: '#6ee7b7', gradient: 'activityLineGradient', value: (d) => d.activity },
                    { key: 'recovery', color: '#a7f3d0', gradient: 'recoveryLineGradient', value: (d) => d.recovery },
                  ].map((metric, metricIndex) => {
                    const points = weeklyData.map((data, index) => {
                      const x = 40 + (index / (weeklyData.length - 1)) * 520
                      const value = metric.value(data)
                      const y = 250 - ((value - minValue) / range) * 200
                      return { x, y, value }
                    })
                    
                    const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
                    
                    return (
                      <g key={metric.key}>
                        <motion.path
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={isInView ? { pathLength: 1, opacity: 1 } : {}}
                          transition={{ delay: 0.5 + metricIndex * 0.1, duration: 1.5 }}
                          d={pathData}
                          stroke={`url(#${metric.gradient})`}
                          strokeWidth="3"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        {points.map((point, i) => (
                          <motion.circle
                            key={i}
                            initial={{ r: 0, opacity: 0 }}
                            animate={isInView ? { r: 4, opacity: 1 } : {}}
                            transition={{ delay: 0.6 + metricIndex * 0.1 + i * 0.05 }}
                            cx={point.x}
                            cy={point.y}
                            fill={metric.color}
                            stroke="white"
                            strokeWidth="2"
                            className="drop-shadow-md"
                          />
                        ))}
                      </g>
                    )
                  })}
                </svg>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                {[
                  { label: t.analytics.sleep, color: '#10b981', iconType: 'sleep' },
                  { label: t.analytics.energy, color: '#34d399', iconType: 'energy' },
                  { label: t.analytics.activity, color: '#6ee7b7', iconType: 'activity' },
                  { label: t.analytics.recovery, color: '#a7f3d0', iconType: 'recovery' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      {item.iconType === 'energy' && (
                        <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M13 2L3 14H12L11 22L21 10H12L13 2Z"
                            fill={`url(#weekly-energy-gradient-${index})`}
                            stroke={`url(#weekly-energy-gradient-${index})`}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <defs>
                            <linearGradient id={`weekly-energy-gradient-${index}`} x1="3" y1="14" x2="21" y2="10">
                              <stop offset="0%" stopColor="#10b981" />
                              <stop offset="100%" stopColor="#34d399" />
                            </linearGradient>
                          </defs>
                        </svg>
                      )}
                      {item.iconType === 'sleep' && (
                        <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
                            fill={`url(#weekly-sleep-gradient-${index})`}
                            stroke={`url(#weekly-sleep-gradient-${index})`}
                            strokeWidth="1.5"
                          />
                          <path
                            d="M9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12Z"
                            fill="white"
                          />
                          <defs>
                            <linearGradient id={`weekly-sleep-gradient-${index}`} x1="11.21" y1="3" x2="21" y2="12.79">
                              <stop offset="0%" stopColor="#10b981" />
                              <stop offset="100%" stopColor="#059669" />
                            </linearGradient>
                          </defs>
                        </svg>
                      )}
                      {item.iconType === 'activity' && (
                        <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M22 12H18L15 21L9 3L6 12H2"
                            stroke={`url(#weekly-activity-gradient-${index})`}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <defs>
                            <linearGradient id={`weekly-activity-gradient-${index}`} x1="2" y1="3" x2="22" y2="21">
                              <stop offset="0%" stopColor="#10b981" />
                              <stop offset="100%" stopColor="#059669" />
                            </linearGradient>
                          </defs>
                        </svg>
                      )}
                      {item.iconType === 'recovery' && (
                        <svg className="w-full h-full" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M20.84 4.61C20.3292 4.099 19.7228 3.69364 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69364 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04097 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7564 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22248 22.4518 8.5C22.4518 7.77752 22.3095 7.0621 22.0329 6.39464C21.7564 5.72718 21.351 5.12075 20.84 4.61Z"
                            fill={`url(#weekly-recovery-gradient-${index})`}
                          />
                          <defs>
                            <linearGradient id={`weekly-recovery-gradient-${index}`} x1="1.55" y1="3" x2="22.45" y2="21.23">
                              <stop offset="0%" stopColor="#10b981" />
                              <stop offset="100%" stopColor="#059669" />
                            </linearGradient>
                          </defs>
                        </svg>
                      )}
                    </div>
                    <span className="text-sm text-gray-600 font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-3xl p-8 border-2 border-gray-100 premium-shadow"
        >
          <h3 className="text-2xl font-bold mb-6 text-gray-900 text-center">
            {t.analytics.improvementTrends}
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { label: t.analytics.averageEnergy, trend: '+34%', period: t.analytics.period },
              { label: t.analytics.sleepQualityLabel, trend: '+28%', period: t.analytics.period },
              { label: t.analytics.recoveryLabel, trend: '+42%', period: t.analytics.period },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="text-center p-6 bg-gradient-to-br from-emerald-50 to-white rounded-2xl border-2 border-primary/20"
              >
                <div className="text-3xl font-black gradient-text mb-2">
                  {item.trend}
                </div>
                <div className="font-semibold text-gray-800 mb-1">{item.label}</div>
                <div className="text-sm text-gray-600">{item.period}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Analytics
